import {
  BanknotesIcon,
  BellAlertIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  ClockIcon,
  DocumentMagnifyingGlassIcon,
  EyeSlashIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, SVGProps } from "react";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;
type Pain = { icon: Icon; title: string; body: string };

/**
 * The landing speaks to two different buyers, toggled by the hero switch:
 * - `imprenta`: has the machines, receives files, prints.
 * - `broker`: a studio that designs and outsources the printing.
 * Their pains and workflow differ (a badly-built file is the printer's
 * problem; the broker isn't a print shop), so the hero, problem, how-it-works
 * and feature set adapt per audience.
 */
export type Audience = "imprenta" | "broker";

export const AUDIENCE_LABELS: Record<Audience, string> = {
  imprenta: "Imprentas",
  broker: "Estudios",
};

/** Per-audience accent, applied theme-wide by overriding `--accent` at the
 * app root: the magenta brand for the printer, a cyan/celeste for the broker.
 * Literal colors (not `var(--accent)`) so the override isn't self-referential. */
export const AUDIENCE_ACCENT: Record<Audience, string> = {
  imprenta: "oklch(56% 0.2179 3.42)",
  broker: "oklch(58% 0.14 233)",
};

export const HERO: Record<Audience, { titleLead: string; titleAccent: string; sub: string }> = {
  imprenta: {
    titleLead: "Toda tu imprenta,",
    titleAccent: "en un solo tablero.",
    sub: "El software para imprentas que reúne todos tus encargos en un solo tablero, del pedido a la entrega. Menos reprocesos, cero pedidos perdidos.",
  },
  broker: {
    titleLead: "Todos tus trabajos,",
    titleAccent: "en un solo tablero.",
    sub: "El software para estudios que diseñan y tercerizan la impresión. Lleva cada trabajo ordenado y a tus clientes al día, del pedido a la entrega.",
  },
};

export const PROBLEM: Record<Audience, { title: string; intro: string; pains: Pain[] }> = {
  imprenta: {
    title: "El caos no está en las máquinas. Está en la coordinación.",
    intro: "Cuatro cosas que se repiten en el día a día y se comen tu margen y tu tiempo.",
    pains: [
      {
        icon: DocumentMagnifyingGlassIcon,
        title: "El archivo mal armado",
        body: "El diseñador arma sin conocer los requisitos, revisas a mano y devuelves un “está mal” por WhatsApp. Empieza un ida y vuelta de días, y cada error que se cuela es una plancha rehecha.",
      },
      {
        icon: ClockIcon,
        title: "Los plazos que no se respetan",
        body: "Las fechas se prometen de palabra, se mueven en silencio, y el cliente se entera del atraso cuando viene a retirar.",
      },
      {
        icon: ChatBubbleLeftRightIcon,
        title: "Pedidos por todos lados",
        body: "WhatsApp, correo, un cuaderno y una planilla. Nadie sabe con certeza qué entró hoy ni en qué quedó cada trabajo.",
      },
      {
        icon: EyeSlashIcon,
        title: "“¿En qué estado está lo mío?”",
        body: "Cada consulta de un cliente termina con alguien levantándose a preguntar por el taller. No hay una foto clara de la carga de trabajo.",
      },
    ],
  },
  broker: {
    title: "Diseñar es lo tuyo. El desorden, no.",
    intro: "Cuatro cosas que se repiten cuando gestionas muchos trabajos a la vez.",
    pains: [
      {
        icon: ChatBubbleLeftRightIcon,
        title: "Pedidos por todos lados",
        body: "WhatsApp, correo, un cuaderno y una planilla. No sabes con certeza qué entró ni en qué quedó cada trabajo.",
      },
      {
        icon: EyeSlashIcon,
        title: "Tus clientes preguntando",
        body: "Te piden novedades y te pones a buscar en el chat para poder contestar algo con certeza.",
      },
      {
        icon: UserGroupIcon,
        title: "El historial se pierde",
        body: "“¿Qué le cobré la vez pasada? ¿Qué archivo era?”, y a revolver conversaciones viejas para encontrarlo.",
      },
      {
        icon: Squares2X2Icon,
        title: "Cada pedido desde cero",
        body: "Trabajos que se repiten y vuelves a cargar los mismos datos una y otra vez, a mano.",
      },
    ],
  },
};

/**
 * Core capabilities, framed as outcomes. `only` restricts a feature to one
 * audience (the file/prepress ones are printer-specific). `tint` gives each
 * card its own hue; `badge` marks work in progress.
 */
export const FEATURES: {
  icon: Icon;
  title: string;
  body: string;
  tint: string;
  badge?: string;
  only?: Audience;
}[] = [
  {
    icon: Squares2X2Icon,
    title: "Tablero configurable",
    body: "Columnas que renombras, reordenas y ajustas a tu operación. Si trabajas en equipo, cada rol ve solo lo suyo; si eres tú solo, es tu tablero y listo.",
    tint: "var(--accent)",
  },
  {
    icon: BookOpenIcon,
    title: "Catálogo que valida",
    body: "Cada producto con sus especificaciones y su plantilla descargable. Al recibir el archivo, el sistema chequea lo básico —medidas, sangrado, color, resolución— antes de que llegue a máquina.",
    tint: "oklch(60% 0.15 240)",
    only: "imprenta",
  },
  {
    icon: CheckBadgeIcon,
    title: "Preprensa como control de calidad",
    body: "Todo lo que va a producción pasa por revisión, venga del enlace público o de tu diseñador, con motivos de rechazo de un clic.",
    tint: "oklch(64% 0.13 175)",
    only: "imprenta",
  },
  {
    icon: BellAlertIcon,
    title: "El cliente sabe en qué anda",
    body: "Cada vez que el pedido avanza, al cliente le llega un aviso por correo automático. Se acabó el “¿ya está lo mío?” por teléfono.",
    tint: "oklch(68% 0.15 60)",
    badge: "WhatsApp próximamente",
  },
  {
    icon: BanknotesIcon,
    title: "Estado de pago a la vista",
    body: "Un semáforo simple —pagado, parcial o no pagado— para que sepas si puedes entregar. Sin módulos financieros de por medio.",
    tint: "oklch(58% 0.19 300)",
  },
  {
    icon: UserGroupIcon,
    title: "Clientes e historial",
    body: "Cada cliente con su historial. Duplicas un encargo anterior en un clic —copia ítems y archivos— y arranca como cobro nuevo.",
    tint: "oklch(62% 0.15 150)",
  },
];

/** Frequently asked questions — different buyer, different questions.
 * Pre-launch: no promised dates, no promised features; just the obvious ones
 * for the audience the visitor chose. Plain language, no jargon, no Spanglish.
 */
export const FAQS: Record<Audience, { q: string; a: string }[]> = {
  imprenta: [
    {
      q: "¿Tengo que instalar algo?",
      a: "No. Ciam es una plataforma web — funciona desde cualquier navegador, sin instalar nada en los computadores del taller.",
    },
    {
      q: "¿Mis clientes tienen que pagar o registrarse?",
      a: "No. Ellos acceden a sus encargos por un enlace que les llega al correo, sin crear cuenta ni instalar nada. Ciam hoy es gratis durante esta etapa.",
    },
    {
      q: "¿Funciona si atiendo tanto a diseñadores con archivo listo como a clientes que piden que les diseñe?",
      a: "Sí. Ciam cubre los dos casos: el diseñador que llega con su PDF listo, y el cliente que llega al mostrador sin nada y necesita que le armes el diseño. Cada uno sigue su propio flujo dentro del mismo tablero.",
    },
    {
      q: "¿Tengo que cargar todo mi catálogo antes de empezar?",
      a: "No. Cargás los productos que necesitás hoy y vas sumando el resto a medida que los uses. La idea es que empieces con poco y crezca con tu operación.",
    },
    {
      q: "¿Qué pasa con los encargos que llegan por WhatsApp o por teléfono?",
      a: "Los carga tu equipo al sistema — igual que hoy, solo que en vez de quedar en un chat o un cuaderno, queda registrado en el tablero y visible para todo el equipo.",
    },
    {
      q: "¿Reemplaza mi WhatsApp?",
      a: "No lo elimina, lo ordena. Los archivos, las correcciones y las aprobaciones dejan de vivir en un chat interminable y pasan a vivir en el encargo correspondiente. Hoy las notificaciones salen por correo; WhatsApp como canal de notificaciones está en camino.",
    },
    {
      q: "¿Mis operarios, diseñadores y vendedores tienen que ver lo mismo?",
      a: "No. Cada rol ve solo lo que le corresponde: producción ve su cola de trabajo, ventas ve los encargos y montos, preprensa ve los archivos pendientes de revisión.",
    },
    {
      q: "¿Ciam valida automáticamente si un archivo está bien armado?",
      a: "Sí, lo básico: tamaño de página, sangrado, modo de color y resolución. Si algo no cumple, el cliente lo sabe apenas sube el archivo — antes de que llegue a tu mesa de preprensa. Lo que las máquinas no pueden verificar lo revisás vos y rechazás con un motivo específico.",
    },
    {
      q: "¿Y si tengo productos con requisitos distintos según el trabajo?",
      a: "Cada producto del catálogo tiene sus propias especificaciones. Una etiqueta troquelada puede pedir cosas distintas a un vinilo o a un trabajo de gran formato — cada uno con sus propios campos.",
    },
    {
      q: "¿Puedo registrar si un cliente ya pagó?",
      a: "Sí. Cada encargo tiene un campo simple de estado de pago (pagado / parcial / no pagado), editable en cualquier momento. No es un módulo de facturación — es un semáforo para que sepas si podés entregar.",
    },
  ],
  broker: [
    {
      q: "¿Ciam es solo para imprentas?",
      a: "No. Sirve para cualquier negocio que gestione encargos de producción gráfica — sea una agencia de diseño, un broker o un taller que terceriza parte de su producción. El sistema es el mismo; cada uno lo usa según su flujo.",
    },
    {
      q: "¿Tengo que pagar para usarlo?",
      a: "No. Durante esta etapa Ciam es gratis. Cuando definamos los planes, los que están en la lista desde ahora se enteran primero.",
    },
    {
      q: "¿En qué me ayuda si yo no produzco, sino que tercerizo?",
      a: "A ordenar todo lo que pasa antes y después de mandar a producir: la comunicación con tu cliente, las versiones del diseño, las aprobaciones, los estados de cada trabajo y el historial. En vez de manejar todo por WhatsApp y correo, lo tenés en un solo tablero.",
    },
    {
      q: "¿Puedo gestionar encargos de varios clientes a la vez?",
      a: "Sí. El tablero muestra todos tus encargos activos en columnas, ordenados por urgencia. De un vistazo sabés qué está esperando aprobación, qué está en producción y qué se entrega esta semana.",
    },
    {
      q: "¿Cómo encargan trabajo mis clientes?",
      a: "Les pasás tu propio enlace público: eligen el servicio, ven las especificaciones, suben su archivo y mandan el encargo — sin llamadas ni mensajes para coordinar el formato.",
    },
    {
      q: "¿Puedo manejar clientes que llegan sin archivo, que necesitan diseño?",
      a: "Sí. Ese encargo lo cargás vos al sistema, le asignás a tu diseñador, subís las versiones del arte, y el cliente aprueba el diseño por el mismo enlace — sin tener que instalar nada.",
    },
    {
      q: "¿Cómo le muestro un diseño a mi cliente para que lo apruebe?",
      a: "Subís el archivo al encargo y el sistema genera un enlace de aprobación para tu cliente. Él lo abre desde el teléfono o la compu, lo revisa, y aprueba o pide cambios con un comentario.",
    },
    {
      q: "¿Puedo ver el estado de mis encargos en tiempo real?",
      a: "Sí. Tanto vos desde tu tablero como tu cliente desde su enlace ven el estado actualizado al instante.",
    },
    {
      q: "¿Funciona si trabajo con varios proveedores de producción distintos?",
      a: "Sí. Podés registrar en cada encargo qué proveedor lo está produciendo, como referencia interna. La coordinación directa con el proveedor sigue siendo por los canales que ya usás hoy.",
    },
    {
      q: "¿Puedo repetir encargos anteriores fácilmente?",
      a: "Sí. Desde el historial de cualquier cliente podés duplicar un encargo pasado con un clic — mismo producto, mismo archivo, mismas especificaciones — sin rehacer todo desde cero.",
    },
    {
      q: "¿Necesito crear una cuenta o contraseña para mis clientes?",
      a: "No. Tus clientes acceden a sus encargos por un enlace que les llega al correo, sin contraseñas ni registros.",
    },
  ],
};
